import { h, render, Component } from 'preact';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import util from 'preact-util';

import DyrejournalSalePrescription from '../../components/dyrejournal/salePrescription';
import { printViaIframe } from '../../lib/printHelper';
import PrescriptionPackageTotal from './prescriptionPackageTotal';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appAlert } from '../../lib/dialogs';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

class Blank extends Component {
    constructor(props) {
        super(props);
        this.state = {
            printData: {},
        };
    }

    componentDidUpdate(prevProps) {
        const prev = prevProps.prescriptions || [];
        const curr = this.props.prescriptions || [];
        if (prev.length !== curr.length || prev !== curr) {
            this.loadPrintData();
        }
    }

    loadPrintDataForPrescription = async prescription => {
        const { animalStore, visitorStore, journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        const animalId = prescription.animal || newJournal?.animal;
        if (!animalId) return null;
        try {
            // load() returns undefined when the API responds with an error.
            const currentAnimals = await animalStore.load({ query: { id: animalId }, skipUpdate: true });
            const currentAnimal = currentAnimals?.[0];
            if (!currentAnimal) return null;
            const currentVisitors = await visitorStore.load({ query: { id: currentAnimal.owner }, skipUpdate: true });
            return {
                currentAnimal,
                currentVisitor: currentVisitors?.[0],
            };
        } catch (err) {
            console.error('loadPrintDataForPrescription failed', err);
            return null;
        }
    }

    loadPrintData = async () => {
        const { prescriptions = [] } = this.props;
        if (!prescriptions.length) return;

        const printData = {};
        for (const prescription of prescriptions) {
            const data = await this.loadPrintDataForPrescription(prescription);
            if (!data) continue;
            printData[prescription.id || prescription.md5 || prescription.uuidv4] = data;
        }
        this.setState({ printData });
    }

    onClickPrintPrescription = async (e, prescription) => {
        e.stopPropagation();

        const key = prescription.id || prescription.md5 || prescription.uuidv4;
        const { printData = {} } = this.state;
        let data = printData[key];
        if (!data) {
            // The prefetch in loadPrintData may still be running or may have
            // failed. Load what this one prescription needs right now.
            data = await this.loadPrintDataForPrescription(prescription);
            if (data) {
                this.setState(prevState => ({ printData: { ...prevState.printData, [key]: data } }));
            }
        }
        if (!data) {
            (await appAlert('Laster data, vennligst prøv igjen.'));
            return;
        }

        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        const prescriptionComponent = <IntlProvider definition={languageDef}>
            <DyrejournalSalePrescription
                stores={this.props.stores}
                prescription={prescription}
                currentAnimal={data.currentAnimal}
                currentVisitor={data.currentVisitor}
            />;
        </IntlProvider>;
        const tempDiv = document.createElement('div');
        tempDiv.className = 'printPrescription';
        document.body.appendChild(tempDiv);
        render(prescriptionComponent, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);

        printViaIframe(htmlContent);
    }

    onClickEmailPrescription = async (e, prescription) => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.prescription').dataset;
        // console.log('onClickPrescription', id);
        const { drawerLevel = 3, journal } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('emailPrescription', {
            height: drawerHeightMediumLarge,
            prescription,
            journal,
        }, drawerLevel + 1);
    }

    componentDidMount() {
        this.loadPrintData();
    }

    componentDidUpdate() {
    }

    render() {
        const { prescriptions = [], journal } = this.props;
        if (!prescriptions.length) {
            return null;
        }
        return (<div class='w-100 d-flex justify-content-center position-relative'>
            <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                {prescriptions.map(prescription => {
                    const fields = ['date', 'amountPerAnimal', 'lastThreatment', 'usageReason', 'threatmentBy'];
                    return (
                        <div className='d-flex flex-column mb-2' key={prescription.id}>
                            <strong>{prescription.name}</strong><br />
                            {prescription.drugs && prescription.drugs.length > 0 ? <>
                                {prescription.drugs.map(drug => (
                                    <div key={drug.id}>
                                        {drug.qty || 1} x {drug.rpText || drug.navnFormStyrke} ({drug.pakningsinfoPakningsstr || drug.pakningsinfoMengde} {drug.pakningsinfoEnhetPakningV})
                                        <PrescriptionPackageTotal drug={drug} className='small text-primary fw-semibold mt-1' />
                                        <small class='text-muted'>
                                            {drug.preparattypeDn && <>Type: {drug.preparattypeDn}</>}
                                            {drug.atcDn && <>, <span class='font-weight-lighter'>ATC:</span> {drug.atcDn}</>}
                                            {drug.reseptgruppeV && <>, <span class='font-weight-lighter'>Grp:</span> {drug.reseptgruppeV}</>}
                                            {drug.smakDn && <>, <span class='font-weight-lighter'>Smak:</span> {drug.smakDn}</>}
                                            {drug.administrasjonsveiDn && <>, <span class='font-weight-lighter'>Adm:</span> {drug.administrasjonsveiDn}</>}
                                        </small>
                                    </div>
                                ))}
                            </> : <span class='text-muted'>No drugs prescribed</span>}
                            <br />

                            <div
                                class='d-flex flex-row prescription'
                                data-journal={journal}
                                data-id={prescription.id}
                                data-md5={prescription.md5}
                            >
                                <button
                                    class={`btn btn-danger ms-2 rounded-pill mt-2`}
                                    onClick={(e) => this.onClickPrintPrescription(e, prescription)}
                                >
                                    <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-prescription'>Print prescription</Text>
                                </button>
                                <button
                                    class={`btn btn-primary ms-2 rounded-pill mt-2`}
                                    onClick={(e) => this.onClickEmailPrescription(e, prescription)}
                                >
                                    <i class='fa-solid fa-paper-plane' /> <Text id='dyrejournal.email-prescription'>Email prescription</Text>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>);
    }
}

export default Blank;
